import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Check,
  Eye,
  AlertCircle,
  ShoppingCart,
  PlusCircle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { ServiceChangeRequestDialog } from '@/components/ServiceChangeRequestDialog';
import { determineGstType } from '@/utils/gstUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_CYCLE_MIN_DAYS = {
  monthly: 30,
  quarterly: 90,
  half_yearly: 180,
  yearly: 365,
  two_years: 730,
  three_years: 1095,
};

const ALL_BILLING_CYCLES = [
  { key: 'monthly',     label: 'Monthly' },
  { key: 'quarterly',   label: 'Quarterly (3 months)' },
  { key: 'half_yearly', label: 'Half-yearly (6 months)' },
  { key: 'yearly',      label: 'Yearly' },
  { key: 'two_years',   label: '2 Years' },
  { key: 'three_years', label: '3 Years' },
];

const getClientGstInfo = (gstNumber, stateName) => determineGstType(gstNumber, stateName);

const fmt = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'numeric', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const map = {
    paid:      'bg-green-100 text-green-700 border border-green-200',
    unpaid:    'bg-red-100 text-red-600 border border-red-200',
    pending:   'bg-amber-100 text-amber-700 border border-amber-200',
    cancellation_requested: 'bg-amber-50 text-amber-700 border border-amber-200',
    voided:    'bg-slate-100 text-slate-500 border border-slate-200',
    refunded:  'bg-blue-100 text-blue-700 border border-blue-200',
    processed: 'bg-green-100 text-green-700 border border-green-200',
  };
  const cls = map[status] || map.pending;
  return (
    <span className={`px-2.5 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : '-'}
    </span>
  );
};

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`relative pb-3 text-sm transition-colors whitespace-nowrap ${
      active
        ? 'text-blue-700 font-medium after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-blue-600'
        : 'text-slate-500 hover:text-slate-700'
    }`}
  >
    {children}
  </button>
);

export default function ClientQuotesAndOrders() {
  const location = useLocation();
  const urlTab = new URLSearchParams(location.search).get('tab');
  const VALID_TABS = ['quotes', 'order-requests', 'cancellation', 'service-change'];

  const [activeTab, setActiveTab] = useState(VALID_TABS.includes(urlTab) ? urlTab : 'quotes');

  useEffect(() => {
    if (urlTab && VALID_TABS.includes(urlTab)) setActiveTab(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);

  // ── data ──
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [cancelRequests, setCancelRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderReqs, setOrderReqs] = useState([]);
  const [clientInfo, setClientInfo] = useState({});
  const [reqCategories, setReqCategories] = useState([]);
  const [reqProducts, setReqProducts] = useState([]);

  // ── service change ──
  const [scrHistory, setScrHistory] = useState([]);
  const [scrLoading, setScrLoading] = useState(false);
  const [scrDialogOpen, setScrDialogOpen] = useState(false);
  const [scrSelectedOrder, setScrSelectedOrder] = useState(null);

  // ── cancel request ──
  const [cancelReqOpen, setCancelReqOpen] = useState(false);
  const [cancelOrderId, setCancelOrderId] = useState(null);
  const [cancelProductName, setCancelProductName] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState('');
  const [submittingCancel, setSubmittingCancel] = useState(false);

  // ── order request ──
  const [orderReqOpen, setOrderReqOpen] = useState(false);
  const [orderReqForm, setOrderReqForm] = useState({
    product_category: '',
    product_id: '',
    billing_cycle: 'monthly',
    quantity: 1,
    unit_price: 0,
    validity_days: 30,
    notes: '',
  });
  const [submittingReq, setSubmittingReq] = useState(false);
  const [orderReqErrors, setOrderReqErrors] = useState({});

  // ── quote dialog ──
  const [quoteDialog, setQuoteDialog] = useState(null);
  const [acceptingQuote, setAcceptingQuote] = useState(null);

  // ── revision dialog ──
  const [revisionDialog, setRevisionDialog] = useState(null);
  const [revisionReason, setRevisionReason] = useState('');
  const [submittingRevision, setSubmittingRevision] = useState(false);

  const fetchScrHistory = useCallback(async () => {
    setScrLoading(true);
    try {
      const res = await axios.get(`${API}/service-change-requests`);
      setScrHistory(res.data || []);
    } catch { /* silently fail */ }
    finally { setScrLoading(false); }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, quotesRes, cancelRes, ordersRes, orderReqsRes, productsRes] =
        await Promise.allSettled([
          axios.get(`${API}/auth/me`),
          axios.get(`${API}/client/quotes`),
          axios.get(`${API}/client/cancellation-requests`),
          axios.get(`${API}/orders`),
          axios.get(`${API}/client/order-requests`),
          axios.get(`${API}/client/products-with-pricing`),
        ]);

      if (meRes.status === 'fulfilled') setClientInfo(meRes.value.data || {});
      if (quotesRes.status === 'fulfilled') setQuotes(quotesRes.value.data || []);
      if (cancelRes.status === 'fulfilled') setCancelRequests(cancelRes.value.data || []);
      if (ordersRes.status === 'fulfilled') setOrders(ordersRes.value.data?.orders || ordersRes.value.data || []);
      if (orderReqsRes.status === 'fulfilled') setOrderReqs(orderReqsRes.value.data || []);

      if (productsRes.status === 'fulfilled') {
        const prods = productsRes.value.data || [];
        const cats = [...new Set(prods.map(p => p.category).filter(Boolean))];
        setReqCategories(cats);
        setReqProducts(prods);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchScrHistory();
  }, [fetchAll, fetchScrHistory]);

  // Load products when order request dialog opens
  useEffect(() => {
    if (!orderReqOpen) return;
    axios.get(`${API}/client/products-with-pricing`).catch(() => ({ data: [] })).then(res => {
      const prods = res.data || [];
      const cats = [...new Set(prods.map(p => p.category).filter(Boolean))];
      setReqCategories(cats);
      setReqProducts(prods);
    });
  }, [orderReqOpen]);

  const handleApproveQuote = async (quoteId) => {
    setAcceptingQuote(quoteId);
    try {
      await axios.post(`${API}/client/quotes/${quoteId}/approve`);
      toast.success('Quote approved');
      setQuoteDialog(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setAcceptingQuote(null);
    }
  };

  const handleRequestUpdate = async () => {
    if (!revisionReason.trim()) { toast.error('Please describe the changes you need'); return; }
    setSubmittingRevision(true);
    try {
      await axios.post(`${API}/client/quotes/${revisionDialog.id}/request-update`, { reason: revisionReason });
      toast.success('Update request sent to your partner');
      setRevisionDialog(null);
      setRevisionReason('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmittingRevision(false); }
  };

  const openCancelRequest = (order) => {
    setCancelOrderId(order.id);
    setCancelProductName(order.product_name || 'Service');
    setCancelReason('');
    setCancelReasonError('');
    setCancelReqOpen(true);
  };

  const handleSubmitCancelRequest = async () => {
    if (!cancelReason.trim()) {
      setCancelReasonError('Please provide a reason for cancellation');
      return;
    }
    setSubmittingCancel(true);
    try {
      await axios.post(`${API}/client/orders/${cancelOrderId}/cancel`, { reason: cancelReason.trim() });
      toast.success('Cancellation request submitted. Admin will review it shortly.');
      setCancelReqOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit cancellation request');
    } finally {
      setSubmittingCancel(false);
    }
  };

  const handleSubmitOrderRequest = async () => {
    const errors = {};
    if (!orderReqForm.product_category) errors.product_category = 'Category is required';
    if (!orderReqForm.product_id) errors.product_id = 'Product is required';
    const minDays = BILLING_CYCLE_MIN_DAYS[orderReqForm.billing_cycle] || 30;
    if ((Number(orderReqForm.validity_days) || 0) < minDays) {
      errors.validity_days = `Minimum ${minDays} days for ${orderReqForm.billing_cycle} cycle`;
    }
    if (Object.keys(errors).length > 0) { setOrderReqErrors(errors); return; }
    setOrderReqErrors({});
    setSubmittingReq(true);
    try {
      const selectedProduct = reqProducts.find(p => p.id === orderReqForm.product_id);
      const subtotal = (orderReqForm.unit_price || 0) * (orderReqForm.quantity || 1);
      await axios.post(`${API}/client/order-requests`, {
        product_category: orderReqForm.product_category,
        product_id: orderReqForm.product_id,
        product_name: selectedProduct?.name || '',
        billing_cycle: orderReqForm.billing_cycle,
        quantity: Number(orderReqForm.quantity) || 1,
        unit_price: orderReqForm.unit_price || 0,
        validity_days: Number(orderReqForm.validity_days) || 7,
        subtotal,
        notes: orderReqForm.notes,
      });
      toast.success('Order request submitted successfully');
      setOrderReqOpen(false);
      setOrderReqForm({ product_category: '', product_id: '', billing_cycle: 'monthly', quantity: 1, unit_price: 0, validity_days: 30, notes: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingReq(false);
    }
  };

  const cancellableOrders = orders.filter((o) =>
    ['pending', 'active', 'processing', 'provisioned'].includes(o.status)
  );

  return (
    <DashboardLayout role="client">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <h1 className="text-2xl font-bold text-slate-800">Quotes &amp; Orders</h1>

        {/* Tab Bar */}
        <div className="border-b border-slate-200">
          <div className="flex gap-6 overflow-x-auto">
            <TabBtn active={activeTab === 'quotes'} onClick={() => setActiveTab('quotes')} testId="tab-my-quotes">
              My Quotes
            </TabBtn>
            <TabBtn active={activeTab === 'order-requests'} onClick={() => setActiveTab('order-requests')} testId="tab-order-requests">
              Order Requests
            </TabBtn>
            <TabBtn active={activeTab === 'cancellation'} onClick={() => setActiveTab('cancellation')} testId="tab-cancellation">
              Cancellation Requests
            </TabBtn>
            <TabBtn active={activeTab === 'service-change'} onClick={() => setActiveTab('service-change')} testId="tab-service-change">
              Service Change
            </TabBtn>
          </div>
        </div>

        {/* ─── My Quotes Tab ─── */}
        {activeTab === 'quotes' && (
          <Card className="border border-slate-200 shadow-sm overflow-hidden" data-testid="quotes-tab-content">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    {['Quote #', 'Product', 'Date', 'Valid Until', 'Status', 'Action'].map((h) => (
                      <th key={h} className="text-left px-5 py-3 text-slate-600 font-medium text-xs">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="px-5 py-10 text-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                    </td></tr>
                  ) : quotes.length === 0 ? (
                    <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No quotes found</td></tr>
                  ) : quotes.map((q) => (
                    <tr key={q.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-5 py-3 font-medium text-blue-600">Q-{q.id}</td>
                      <td className="px-5 py-3 text-slate-700">{q.product_name}</td>
                      <td className="px-5 py-3 text-slate-500">{fmtDate(q.created_at)}</td>
                      <td className="px-5 py-3 text-slate-500">{fmtDate(q.valid_until)}</td>
                      <td className="px-5 py-3"><StatusBadge status={q.status} /></td>
                      <td className="px-5 py-3">
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" className="text-xs" onClick={() => setQuoteDialog(q)}
                            data-testid={`view-quote-${q.id}`}>
                            <Eye className="h-3 w-3 mr-1" /> View {q.version > 1 && <span className="ml-0.5 text-blue-600">v{q.version}</span>}
                          </Button>
                          {(q.status === 'draft' || q.status === 'revision_requested') && (
                            <>
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                                onClick={() => handleApproveQuote(q.id)} disabled={acceptingQuote === q.id}
                                data-testid={`approve-quote-${q.id}`}>
                                {acceptingQuote === q.id
                                  ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                  : <><Check className="h-3 w-3 mr-1" />Approve</>
                                }
                              </Button>
                              {q.status === 'draft' && (
                                <Button size="sm" variant="outline"
                                  className="text-xs border-amber-300 text-amber-700 hover:bg-amber-600 hover:border-amber-600"
                                  onClick={() => { setRevisionDialog(q); setRevisionReason(''); }}
                                  data-testid={`request-update-${q.id}`}>
                                  Request Update
                                </Button>
                              )}
                            </>
                          )}
                          {q.status === 'revision_requested' && (
                            <span className="text-xs text-amber-600 font-medium">Update Pending</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* ─── Order Requests Tab ─── */}
        {activeTab === 'order-requests' && (
          <div className="space-y-4" data-testid="order-requests-tab-content">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-500">Submit a request to your partner for a new service. Track its progress below.</p>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                onClick={() => setOrderReqOpen(true)}
                data-testid="new-order-request-btn"
              >
                <PlusCircle className="h-4 w-4 mr-2" />New Request
              </Button>
            </div>
            <Card className="border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b">
                      {['Order ID', 'Product / Service', 'Billing Cycle', 'Qty', 'Submitted', 'Status'].map((h) => (
                        <th key={h} className="text-left px-5 py-3 text-slate-600 font-medium text-xs">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} className="px-5 py-10 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                      </td></tr>
                    ) : orderReqs.length === 0 ? (
                      <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">
                        <ShoppingCart className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                        No order requests yet. Click "New Request" to get started.
                      </td></tr>
                    ) : orderReqs.map((r) => (
                      <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`order-req-row-${r.id}`}>
                        <td className="px-5 py-3 font-mono text-xs text-slate-500">{r.id}</td>
                        <td className="px-5 py-3 text-slate-700">{r.product_name || r.product_category || '-'}</td>
                        <td className="px-5 py-3 text-slate-600 capitalize">{r.billing_cycle?.replace('_', ' ')}</td>
                        <td className="px-5 py-3 text-slate-600">{r.quantity}</td>
                        <td className="px-5 py-3 text-slate-500">{fmtDate(r.created_at)}</td>
                        <td className="px-5 py-3">
                          <span className={`px-2.5 py-0.5 rounded text-xs font-medium ${
                            r.status === 'active' ? 'bg-green-100 text-green-700' :
                            r.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                            r.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                            r.status === 'client_requested' ? 'bg-purple-100 text-purple-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {r.status === 'pending' ? 'Submitted' :
                             r.status === 'client_requested' ? 'Pending Review' :
                             r.status === 'processing' ? 'In Progress' :
                             r.status === 'active' ? 'Activated' :
                             r.status === 'cancelled' ? 'Cancelled' :
                             (r.status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ─── Cancellation Requests Tab ─── */}
        {activeTab === 'cancellation' && (
          <div className="space-y-4" data-testid="cancellation-tab-content">
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <h2 className="font-semibold text-slate-800 mb-2">Request Order Cancellation</h2>
                <p className="text-sm text-slate-500">Select an active order below to submit a cancellation request.</p>

                {loading ? (
                  <div className="flex justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : cancellableOrders.length > 0 ? (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 text-slate-500 font-medium">Order #</th>
                          <th className="text-left py-2 text-slate-500 font-medium">Product</th>
                          <th className="text-left py-2 text-slate-500 font-medium">Status</th>
                          <th className="text-left py-2 text-slate-500 font-medium">Date</th>
                          <th className="text-right py-2 text-slate-500 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cancellableOrders.map((o) => (
                          <tr key={o.id} className="border-b last:border-0">
                            <td className="py-3 font-medium text-slate-700">{o.order_number || o.id}</td>
                            <td className="py-3 text-slate-700">{o.product_name || '-'}</td>
                            <td className="py-3"><StatusBadge status={o.status} /></td>
                            <td className="py-3 text-slate-500">{fmtDate(o.created_at)}</td>
                            <td className="py-3 text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-600 hover:border-red-600 text-xs"
                                onClick={() => openCancelRequest(o)}
                                data-testid={`cancel-order-${o.id}`}
                              >
                                Request Cancellation
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 mt-3">No active orders available for cancellation.</p>
                )}
              </CardContent>
            </Card>

            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <h2 className="font-semibold text-slate-800 mb-3">Cancellation History</h2>
                {cancelRequests.length === 0 ? (
                  <p className="text-sm text-slate-500">No cancellation requests found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 text-slate-500 font-medium">Product</th>
                          <th className="text-left py-2 text-slate-500 font-medium">Domain</th>
                          <th className="text-left py-2 text-slate-500 font-medium">Status</th>
                          <th className="text-left py-2 text-slate-500 font-medium">Admin Notes</th>
                          <th className="text-left py-2 text-slate-500 font-medium">Requested</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cancelRequests.map((r) => (
                          <tr key={r.id} className="border-b last:border-0" data-testid={`cancel-req-row-${r.id}`}>
                            <td className="py-3 text-slate-700">{r.product_name || '-'}</td>
                            <td className="py-3 text-slate-500">{r.domain || '-'}</td>
                            <td className="py-3">
                              <span className={`px-2.5 py-0.5 rounded text-xs font-medium ${
                                r.status === 'approved' ? 'bg-green-100 text-green-700' :
                                r.status === 'rejected' ? 'bg-red-100 text-red-600' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {r.status === 'pending' ? 'Pending Review' :
                                 r.status === 'approved' ? 'Approved' :
                                 r.status === 'rejected' ? 'Rejected' :
                                 (r.status || '').replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td className="py-3 text-slate-500 max-w-[200px]">
                              {r.admin_notes ? (
                                <span className={r.status === 'rejected' ? 'text-red-600 text-xs' : 'text-slate-500 text-xs'}>
                                  {r.admin_notes}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="py-3 text-slate-500">{fmtDate(r.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ─── Service Change Tab ─── */}
        {activeTab === 'service-change' && (
          <div className="space-y-4" data-testid="service-change-tab-content">
            <p className="text-sm text-slate-500">Request renewal, upgrade, or downgrade on an active service.</p>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Select Service to Change</p>
              <div className="grid gap-3">
                {loading ? (
                  <div className="flex justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : orders.filter(o => ['active', 'pending', 'processing'].includes(o.status)).length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">No active services to change</div>
                ) : (
                  orders.filter(o => ['active', 'pending', 'processing'].includes(o.status)).map(order => (
                    <div key={order.id} className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors">
                      <div>
                        <p className="font-medium text-slate-800 text-sm">{order.product_name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          ₹{(order.total_amount ?? order.amount ?? 0).toLocaleString()} &bull; {order.status}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-blue-600 border-blue-200 hover:bg-blue-600 hover:border-blue-600 text-xs"
                        onClick={() => { setScrSelectedOrder(order); setScrDialogOpen(true); }}
                        data-testid={`request-change-${order.id}`}
                      >
                        Request Change
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* SCR History */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Request History</p>
                <Button variant="ghost" size="sm" className="text-xs text-slate-400 h-6" onClick={fetchScrHistory}
                  data-testid="refresh-scr-history-btn">
                  <RefreshCw className="h-3 w-3 mr-1" />Refresh
                </Button>
              </div>
              <Card className="border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        {['Service', 'Type', 'New Plan', 'Status', 'Date'].map(h => (
                          <th key={h} className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {scrLoading ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto" />
                        </td></tr>
                      ) : scrHistory.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">
                          No service change requests yet
                        </td></tr>
                      ) : scrHistory.map(scr => (
                        <tr key={scr.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`scr-row-${scr.id}`}>
                          <td className="px-4 py-2.5 text-slate-700 text-xs">{scr.current_product_name}</td>
                          <td className="px-4 py-2.5 capitalize text-xs text-slate-600">{scr.request_type}</td>
                          <td className="px-4 py-2.5 text-xs text-slate-500">{scr.requested_product_name || scr.billing_cycle || '—'}</td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              scr.status === 'approved' ? 'bg-green-100 text-green-700' :
                              scr.status === 'rejected' ? 'bg-red-100 text-red-600' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {scr.status?.charAt(0).toUpperCase() + scr.status?.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-500">
                            {new Date(scr.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* ============================= DIALOGS ============================= */}

        {/* Quote detail dialog */}
        <Dialog open={!!quoteDialog} onOpenChange={(o) => { if (!o) setQuoteDialog(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Quotation Details</DialogTitle>
            </DialogHeader>
            {quoteDialog && (
              <div className="space-y-4">
                <div className="bg-[#2563eb]/10 rounded p-4">
                  <div className="flex justify-between mb-3">
                    <div>
                      <p className="text-xs text-slate-500">Quote #</p>
                      <p className="font-bold text-slate-800">Q-{quoteDialog.id}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Total (incl. GST)</p>
                      <p className="text-xl font-bold text-blue-600">{fmt(quoteDialog.total_amount)}</p>
                    </div>
                  </div>
                  <div className="border-t border-blue-200 pt-2.5 space-y-1 text-sm">
                    <div className="flex justify-between text-slate-500">
                      <span>Sub Total</span>
                      <span>{fmt(quoteDialog.subtotal ?? (quoteDialog.unit_price || 0) * (quoteDialog.quantity || 1))}</span>
                    </div>
                    {quoteDialog.gst_type === 'CGST+SGST' ? (
                      <>
                        <div className="flex justify-between text-slate-500">
                          <span>CGST (9.00%)</span>
                          <span>{fmt((quoteDialog.gst_amount ?? 0) / 2)}</span>
                        </div>
                        <div className="flex justify-between text-slate-500">
                          <span>SGST (9.00%)</span>
                          <span>{fmt((quoteDialog.gst_amount ?? 0) / 2)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between text-slate-500">
                        <span>IGST (18.00%)</span>
                        <span>{fmt(quoteDialog.gst_amount ?? Math.round((quoteDialog.subtotal ?? (quoteDialog.unit_price||0)*(quoteDialog.quantity||1)) * 0.18 * 100)/100)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-slate-800 border-t border-blue-200 pt-1.5">
                      <span>Grand Total</span>
                      <span className="text-blue-700">{fmt(quoteDialog.total_amount)}</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    ['Product', quoteDialog.product_name],
                    ['Billing Cycle', quoteDialog.billing_cycle],
                    ['Quantity', quoteDialog.quantity],
                    ['Unit Price', fmt(quoteDialog.unit_price)],
                    ['Created', fmtDate(quoteDialog.created_at)],
                    ['Valid Until', fmtDate(quoteDialog.valid_until)],
                  ].map(([l, v]) => (
                    <div key={l}>
                      <p className="text-slate-500 text-xs">{l}</p>
                      <p className="font-medium text-slate-800 capitalize">{v}</p>
                    </div>
                  ))}
                </div>
                {quoteDialog.notes && (
                  <div className="bg-slate-50 rounded p-3 text-sm text-slate-700">{quoteDialog.notes}</div>
                )}
                {quoteDialog.product_features && quoteDialog.product_features.length > 0 && (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1 mt-2" data-testid="client-product-features">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">What's Included</p>
                    <ul className="space-y-1">
                      {quoteDialog.product_features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-blue-800">
                          <span className="text-blue-500 mt-0.5">✓</span> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {quoteDialog.revisions && quoteDialog.revisions.length > 0 && (
                  <div className="border border-amber-100 bg-amber-50 rounded-lg p-3 mt-2">
                    <p className="text-xs font-semibold text-amber-800 mb-2">Revision History ({quoteDialog.revisions.length})</p>
                    <div className="space-y-2">
                      {quoteDialog.revisions.map((rev, i) => (
                        <div key={i} className="text-xs text-slate-700 border-l-2 border-amber-300 pl-2">
                          <span className="font-medium">
                            {rev.type === 'client_revision_request' ? 'You requested:' : 'Updated by partner:'}
                          </span>{' '}{rev.reason || rev.note || '-'}
                          <span className="ml-2 text-slate-400">
                            {new Date(rev.requested_at || rev.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => setQuoteDialog(null)}>Close</Button>
                  {quoteDialog.status === 'draft' && (
                    <>
                      <Button
                        variant="outline"
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => { setQuoteDialog(null); setRevisionDialog(quoteDialog); setRevisionReason(''); }}
                        data-testid="quote-dialog-request-update"
                      >
                        Request Update
                      </Button>
                      <Button
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => handleApproveQuote(quoteDialog.id)}
                        disabled={acceptingQuote === quoteDialog.id}
                        data-testid="quote-dialog-approve-btn"
                      >
                        {acceptingQuote === quoteDialog.id
                          ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          : <Check className="h-4 w-4 mr-2" />}
                        Approve Quote
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Request Update (Revision) Dialog */}
        <Dialog open={!!revisionDialog} onOpenChange={(o) => { if (!o) setRevisionDialog(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request Quote Update</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-[#115ab4]/10 rounded p-3 text-sm">
                <p className="font-medium">{revisionDialog?.product_name}</p>
                <p className="text-slate-500">Version {revisionDialog?.version || 1} — ₹{revisionDialog?.total_amount?.toLocaleString()}</p>
              </div>
              <div className="space-y-1">
                <Label>What changes do you need? <span className="text-red-500">*</span></Label>
                <Textarea
                  placeholder="e.g. Please reduce quantity to 5, or need a different billing cycle..."
                  value={revisionReason}
                  onChange={e => setRevisionReason(e.target.value)}
                  rows={4}
                  data-testid="revision-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRevisionDialog(null)}>Cancel</Button>
              <Button
                disabled={submittingRevision || !revisionReason.trim()}
                onClick={handleRequestUpdate}
                data-testid="submit-revision-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {submittingRevision ? 'Sending...' : 'Send Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Request Cancellation Dialog */}
        <Dialog open={cancelReqOpen} onOpenChange={(o) => {
          if (!o) { setCancelReqOpen(false); setCancelReason(''); setCancelReasonError(''); }
        }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-slate-800">
                <AlertCircle className="h-5 w-5 text-amber-500" />Request Cancellation
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-1">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                You are requesting cancellation for <strong>{cancelProductName}</strong>. Admin will review and approve/reject your request.
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Reason for Cancellation *</label>
                <textarea
                  className={`w-full border rounded-md text-sm px-3 py-2 min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 ${cancelReasonError ? 'border-red-400' : 'border-slate-200'}`}
                  placeholder="Please provide a reason for cancellation..."
                  value={cancelReason}
                  onChange={e => { setCancelReason(e.target.value); if (cancelReasonError) setCancelReasonError(''); }}
                  data-testid="cancel-reason-input"
                />
                {cancelReasonError && <p className="text-xs text-red-500 mt-1">{cancelReasonError}</p>}
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCancelReqOpen(false)}>Keep Order</Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={handleSubmitCancelRequest}
                  disabled={submittingCancel}
                  data-testid="confirm-cancel-request"
                >
                  {submittingCancel ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : null}
                  Submit Request
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Order Request Form Dialog */}
        <Dialog open={orderReqOpen} onOpenChange={setOrderReqOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>New Order Request</DialogTitle>
              <DialogDescription>Request a new service from your partner. Fill in the details below.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-1 max-h-[70vh] overflow-y-auto pr-1">

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Category *</label>
                <select
                  className={`w-full border rounded-md text-sm px-3 py-2 bg-white ${orderReqErrors.product_category ? 'border-red-400' : 'border-slate-200'}`}
                  value={orderReqForm.product_category}
                  onChange={e => {
                    setOrderReqForm(f => ({ ...f, product_category: e.target.value, product_id: '', unit_price: 0 }));
                    setOrderReqErrors(err => ({ ...err, product_category: '' }));
                  }}
                  data-testid="order-req-category"
                >
                  <option value="">Select category</option>
                  {reqCategories.map(cat => {
                    const catName = typeof cat === 'string' ? cat : (cat.name || '');
                    return <option key={catName} value={catName}>{catName}</option>;
                  })}
                </select>
                {orderReqErrors.product_category && <p className="text-xs text-red-500 mt-0.5">{orderReqErrors.product_category}</p>}
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Product *</label>
                <select
                  className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 bg-white"
                  value={orderReqForm.product_id}
                  onChange={e => {
                    const prod = reqProducts.find(p => p.id === e.target.value);
                    const parentPrice = prod?.parent_prices?.[orderReqForm.billing_cycle]
                      ?? prod?.billing_prices?.[orderReqForm.billing_cycle]
                      ?? prod?.base_price ?? 0;
                    setOrderReqForm(f => ({ ...f, product_id: e.target.value, unit_price: parentPrice }));
                  }}
                  data-testid="order-req-product"
                >
                  <option value="">Select product</option>
                  {reqProducts
                    .filter(p => !orderReqForm.product_category || p.category === orderReqForm.product_category)
                    .filter(p => p.is_active !== false)
                    .map(p => <option key={p.id} value={p.id}>{p.name}</option>)
                  }
                </select>
              </div>

              {orderReqForm.product_id && (() => {
                const prod = reqProducts.find(p => p.id === orderReqForm.product_id);
                return prod?.features?.length > 0 ? (
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Product Features</p>
                    <ul className="space-y-1">
                      {prod.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-blue-800">
                          <span className="text-blue-500 mt-0.5">✓</span> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null;
              })()}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Billing Cycle</label>
                  <select
                    className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 bg-white"
                    value={orderReqForm.billing_cycle}
                    onChange={e => {
                      const cycle = e.target.value;
                      const prod = reqProducts.find(p => p.id === orderReqForm.product_id);
                      const parentPrice = prod?.parent_prices?.[cycle] ?? prod?.billing_prices?.[cycle] ?? prod?.base_price ?? 0;
                      const minDays = BILLING_CYCLE_MIN_DAYS[cycle] || 30;
                      setOrderReqForm(f => ({
                        ...f,
                        billing_cycle: cycle,
                        unit_price: f.product_id ? parentPrice : f.unit_price,
                        validity_days: Math.max(f.validity_days, minDays),
                      }));
                    }}
                    data-testid="order-req-billing-cycle"
                  >
                    {(() => {
                      const prod = reqProducts.find(p => p.id === orderReqForm.product_id);
                      const enabledCycles = prod?.enabled_billing_cycles && prod.enabled_billing_cycles.length > 0
                        ? prod.enabled_billing_cycles
                        : ALL_BILLING_CYCLES.map(c => c.key);
                      return ALL_BILLING_CYCLES
                        .filter(c => enabledCycles.includes(c.key))
                        .map(c => <option key={c.key} value={c.key}>{c.label}</option>);
                    })()}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Quantity</label>
                  <Input
                    type="number" min={1}
                    value={orderReqForm.quantity}
                    onChange={e => setOrderReqForm(f => ({ ...f, quantity: e.target.value }))}
                    data-testid="order-req-quantity"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Unit Price (₹)</label>
                  <Input
                    type="number" min={0} step="0.01"
                    value={orderReqForm.unit_price}
                    readOnly disabled
                    className="bg-slate-100 cursor-not-allowed"
                    data-testid="order-req-unit-price"
                  />
                  {!orderReqForm.product_id && (
                    <p className="text-xs text-slate-400 mt-0.5">Select a product to see price</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Valid For (days)</label>
                  <Input
                    type="number" min={1}
                    value={orderReqForm.validity_days}
                    onChange={e => setOrderReqForm(f => ({ ...f, validity_days: e.target.value }))}
                    data-testid="order-req-validity"
                  />
                  {orderReqErrors.validity_days && <p className="text-xs text-red-500 mt-0.5">{orderReqErrors.validity_days}</p>}
                </div>
              </div>

              {(() => {
                const sub = (orderReqForm.unit_price || 0) * (orderReqForm.quantity || 1);
                const gstAmt = Math.round(sub * 0.18 * 100) / 100;
                const tot = Math.round((sub + gstAmt) * 100) / 100;
                const gstType = getClientGstInfo(clientInfo.gst_number, clientInfo.state_name);
                const half = Math.round(gstAmt / 2 * 100) / 100;
                return (
                  <div className="bg-[#115ab4]/10 rounded-lg p-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Sub Total</span>
                      <span className="font-medium">₹{sub.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {gstType === 'CGST+SGST' ? (
                      <>
                        <div className="flex justify-between">
                          <span className="text-slate-600">CGST (9.00%)</span>
                          <span className="font-medium">₹{half.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">SGST (9.00%)</span>
                          <span className="font-medium">₹{half.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-slate-600">IGST (18.00%)</span>
                        <span className="font-medium">₹{gstAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-1 font-semibold">
                      <span>Total</span>
                      <span className="text-blue-700">₹{tot.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Additional Notes (optional)</label>
                <textarea
                  className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 min-h-[60px] resize-none"
                  placeholder="Any special requirements or context"
                  value={orderReqForm.notes}
                  onChange={e => setOrderReqForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="order-req-notes"
                />
              </div>

              <div className="flex justify-end gap-2 pt-1 border-t">
                <Button variant="outline" onClick={() => setOrderReqOpen(false)}>Cancel</Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleSubmitOrderRequest}
                  disabled={submittingReq}
                  data-testid="submit-order-request-btn"
                >
                  {submittingReq
                    ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    : <ShoppingCart className="h-4 w-4 mr-2" />
                  }
                  Submit Request
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Service Change Request Dialog */}
        {scrSelectedOrder && (
          <ServiceChangeRequestDialog
            open={scrDialogOpen}
            onClose={() => { setScrDialogOpen(false); setScrSelectedOrder(null); fetchScrHistory(); }}
            order={scrSelectedOrder}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
